import React, { useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { usePerformanceStatsFiltersStorage } from '../../../../components/stats/filters/useStatsFilterStorage'
import { useInventoryPerformanceStatsQuery, useInventoryTimeseriesQuery } from '../../../../__generated__/graphql'
import { CampaignProgrammaticRoute } from '../../../routeParams'
import { StatsEntity } from '../../../../interfaces/stats'
import FadeWrapper from '../../../../components/transitions/FadeWrapper'
import { PageContent } from '../../../../components/layout/PageContent'
import { Box } from '@mui/material'
import SectionTitle from '../../../../components/form/SectionTitle'
import { Section } from '../../../../components/layout/Section'
import { StatsFilters } from '../../../../components/stats/filters/StatsFilters'
import InventoryPerformanceStatsTable from './InventoryPerformanceStatsTable'
import { useAppTranslations } from '../../../../utils/hooks/useAppTranslations'
import { flatResellersWebsites } from '../../../../utils/hooks/performanceStats/performanceStatsUtils'
import { useTimeseries } from '../../../../utils/hooks/performanceStats/useTimeseries'
import PerformanceStatsGraph from '../../../../components/stats/timeseries/PerformanceStatsGraph'

const Stats: React.FC = () => {
  const t = useAppTranslations()
  const { inventoryId } = useParams<CampaignProgrammaticRoute>()
  const [performanceStatsDatesFilters, setPerformanceStatsDatesFilters] = usePerformanceStatsFiltersStorage()
  const statsVariables = {
    id: inventoryId,
    startDate: performanceStatsDatesFilters.startDate,
    endDate: performanceStatsDatesFilters.endDate,
  }
  const {
    data: performanceStatsData,
    loading: performanceStatsLoading,
    error: performanceStatsError,
  } = useInventoryPerformanceStatsQuery({
    variables: statsVariables,
    notifyOnNetworkStatusChange: true,
  })

  const {
    data: timeseriesQueryData,
    loading: timeseriesLoading,
    error: timeseriesError,
  } = useInventoryTimeseriesQuery({
    variables: {
      ...statsVariables,
      granularity: performanceStatsDatesFilters.granularity,
    },
    notifyOnNetworkStatusChange: true,
  })

  const {
    timeseriesData,
    chartItemsVisibility,
    toggleVisibility,
    chartOptions,
    setChartOptions,
    chartItems,
    dateFormat,
    timezone,
    setDateFormat,
  } = useTimeseries(timeseriesQueryData?.inventory.timeseries, {
    filters: performanceStatsDatesFilters,
    entity: StatsEntity.Inventory,
  })

  const formattedPerformanceStats = useMemo(() => {
    const performanceStats = performanceStatsData?.inventory.performanceStats
    if (!performanceStats) return null
    const websites = flatResellersWebsites(performanceStats.publisherResellers)

    return {
      ...performanceStats,
      resellers: performanceStats.publisherResellers,
      websites,
      timeseries: timeseriesData?.timeseries ?? [],
    }
  }, [performanceStatsData, timeseriesData])

  return (
    <Box
      sx={{
        display: 'flex',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      <PageContent>
        <FadeWrapper>
          <Section sx={{ mb: 0, mt: 2 }}>
            <SectionTitle title={t('stats.title')} isFirst />
            <StatsFilters
              filters={performanceStatsDatesFilters}
              onChange={setPerformanceStatsDatesFilters}
              setChartDateFormat={setDateFormat}
              chartOptions={chartOptions}
              setChartOptions={setChartOptions}
            />
            <PerformanceStatsGraph
              timeseriesData={timeseriesData}
              chartItems={chartItems}
              chartItemsVisibility={chartItemsVisibility}
              toggleVisibility={toggleVisibility}
              isPublisherStats={false}
              dateFormat={dateFormat}
              timezone={timezone}
              chartOptions={chartOptions}
              loading={timeseriesLoading}
              error={timeseriesError}
            />
            <InventoryPerformanceStatsTable
              performanceStats={formattedPerformanceStats}
              dateFormat={dateFormat}
              timezone={timezone}
              loading={performanceStatsLoading || timeseriesLoading}
              error={performanceStatsError}
            />
          </Section>
        </FadeWrapper>
      </PageContent>
    </Box>
  )
}

export default Stats
